import { Card, CardContent } from '@ui/card';
import { Timer } from './timer';
import { Clock, User } from 'lucide-react';
import { cn } from '@lib/utils';

export interface OrderItem {
  id: string;
  product_name: string;
  quantity: number;
  item_price: number;
}

export interface Order {
  id: string;
  orderNumber: number;
  customerName: string;
  customerGrade?: string; // Série do aluno
  items: OrderItem[];
  status: 'fila' | 'preparando' | 'pronto';
  createdAt: Date;
  startedAt?: Date;
  completedAt?: Date;
  totalAmount: number;
}

interface OrderCardProps {
  order: Order;
  className?: string;
  expanded?: boolean;
  orderNumberSize?: number; // Tamanho do texto do número do pedido em rem
}

const statusColors = {
  fila: {
    bg: 'bg-white',
    border: 'border-brand-primary-300',
    text: 'text-black',
    headerBg: 'bg-brand-primary-200',
    headerText: 'text-black',
  },
  preparando: {
    bg: 'bg-white',
    border: 'border-brand-primary-300',
    text: 'text-black',
    headerBg: 'bg-brand-primary-200',
    headerText: 'text-black',
  },
  pronto: {
    bg: 'bg-white',
    border: 'border-brand-primary-300',
    text: 'text-black',
    headerBg: 'bg-brand-primary-200',
    headerText: 'text-black',
  },
};

export function OrderCard({ order, className, expanded = false, orderNumberSize = 3 }: OrderCardProps) {
  const colors = statusColors[order.status];

  const getWaitingTime = () => {
    const now = new Date();
    const start =
      order.status === 'fila'
        ? order.createdAt
        : order.status === 'preparando' && order.startedAt
          ? order.startedAt
          : order.status === 'pronto' && order.completedAt
            ? order.completedAt
            : order.createdAt;

    const diff = now.getTime() - start.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    return minutes;
  };

  // Debug log para verificar dados recebidos
  console.log('🎯 OrderCard Debug - Rendering order:', {
    id: order.id,
    orderNumber: order.orderNumber,
    customerName: order.customerName,
    customerGrade: order.customerGrade,
    status: order.status,
    itemsCount: order.items?.length || 0,
    totalAmount: order.totalAmount,
    createdAt: order.createdAt,
  });

  return (
    <Card
      className={cn(
        expanded ? 'h-auto min-h-32' : 'h-28',
        'cursor-pointer rounded-xl border-0 shadow-lg transition-all duration-300 hover:shadow-xl',
        colors.bg,
        className,
      )}>
      <CardContent className="h-full rounded-b-xl p-0">
        {/* Header - Always Visible */}
        <div className="flex h-28">
          {/* Left Side - Order Number */}
          <div
            className={cn(
              'flex flex-col items-center justify-center rounded-xl text-center shadow-md',
              expanded ? 'rounded-b-none' : 'rounded-xl',
              colors.headerBg,
            )}
            style={{
              width: `${Math.max(112, orderNumberSize * 40)}px`, // Base 112px (28) + ajuste dinâmico
            }}
          >
            {/* <div className="mb-1 text-xs font-medium uppercase tracking-wide text-black">Pedido</div> */}
            <div
              className="bg-white/20 rounded-2 px-2 py-1 font-semibold text-black whitespace-nowrap"
              style={{ fontSize: `${orderNumberSize}rem` }}
            >
              #{order.orderNumber}
            </div>
          </div>

          {/* Right Side - Customer Info */}
          <div className="flex flex-1 flex-col justify-center px-3 py-2">
            {/* Customer Name */}
            <div className="mb-1 flex items-center">
              <User className="mr-2 h-4 w-4 text-black" />
              <span className={cn('text-3xl font-semibold', colors.text)}>{order.customerName}</span>
            </div>

            {/* Customer Grade */}
            {order.customerGrade && <div className="text-lg font-medium text-black">{order.customerGrade}</div>}
          </div>
        </div>

        {/* Expanded Section - Products */}
        {expanded && order.items && order.items.length > 0 && (
          <div className={cn('rounded-xl rounded-t-none border-t border-gray-200 bg-white p-3')}>
            <div className="mb-2 text-xs font-semibold text-black">Produtos:</div>
            <div className="space-y-1">
              {order.items.map((item, index) => (
                <div key={item.id || index} className="flex items-center justify-between text-xs">
                  <span className="flex-1 text-black">{item.product_name}</span>
                  <span className="rounded bg-gray-100 px-2 py-1 font-semibold text-black">{item.quantity}x</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
